import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_MARK_URL, NAV_LINKS } from "@/lib/constants";
import { useNavHandler } from "@/hooks/useNavHandler";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavHandler();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handle = (href) => (e) => {
    setOpen(false);
    nav(href)(e);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/92 backdrop-blur-md border-b border-border/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <a
          href="/"
          onClick={handle("/")}
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
          aria-label="Duneworth — home"
        >
          <img
            src={LOGO_MARK_URL}
            alt="Duneworth"
            className="h-10 w-10 object-contain shrink-0"
            data-testid="header-logo-mark"
          />
          <span className="hidden sm:inline font-serif text-xl xl:text-2xl tracking-[0.16em] xl:tracking-[0.18em] text-primary uppercase whitespace-nowrap">
            Duneworth
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={handle(link.href)}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="text-[12px] xl:text-[13px] uppercase tracking-[0.18em] xl:tracking-[0.2em] text-primary/80 hover:text-primary transition-colors duration-300 relative group whitespace-nowrap"
            >
              {link.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center">
          <a
            href="/#contact"
            onClick={handle("/#contact")}
            data-testid="header-cta-partner"
            className="bg-primary text-primary-foreground hover:bg-primary/85 px-5 xl:px-6 py-3 text-[11px] xl:text-[12px] uppercase tracking-[0.2em] xl:tracking-[0.22em] transition-colors duration-300 whitespace-nowrap"
          >
            Partner With Us
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 text-primary"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-background border-t border-border"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={handle(link.href)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className="text-sm uppercase tracking-[0.25em] text-primary"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/#contact"
              onClick={handle("/#contact")}
              data-testid="mobile-cta-partner"
              className="bg-primary text-primary-foreground px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-center mt-2"
            >
              Partner With Us
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
